import React, { ReactElement, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../models/stores/Root';
import Header from '../../components/framework/Header';
import Footer from '../../components/framework/Footer';
import CheckoutPageContainer from '../../components/framework/CheckoutPageContainer';
import CartSummarySection from '../../components/framework/CartSummarySection';
import Divider from '../../components/content/Divider';
import OneClickView from '../views/OneClickView';
import EditPaymentView from '../views/EditPaymentView';
import { AddressContract } from '../../types/Address';
import {
  ICustomerBillingAddressModel,
  ICustomerShippingAddressModel,
} from '../../models/stores/sub-stores/Customer';
import { CheckoutEvent } from '../../models/machines/checkout-machine';
import EditShipToView from '../views/EditShipToView';
import EditBillToView from '../views/EditBillToView';

const OneClickPage: React.FC = observer(() => {
  const rootStore = useMst();
  const { configuration, customer, cart, currentStep } = rootStore;
  const checkoutDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentStep === 'OneClick.WalletValidation.CheckAll') {
      rootStore.sendWalletValidationEvent();
    }
  });

  useEffect(() => {
    if (currentStep === 'OneClick.Loading') {
      rootStore.setCartValuesFromCustomer();
    }
  }, [
    customer.isLoading,
    cart.shippingAddress,
    customer.preferredShippingAddress,
    rootStore,
    currentStep,
  ]);

  const scrollTopOfPage = (): void => {
    if (checkoutDivRef.current?.scrollIntoView) {
      checkoutDivRef.current.scrollIntoView();
    }
  };

  const handleLogoutClick = async (): Promise<void> => {
    await customer.logoutFlow();
  };

  const handleEditShipToClicked = (): void => {
    if (cart.shippingAddress.id) {
      rootStore.send({ type: 'EDIT_SHIPPING' });
    } else {
      rootStore.send({ type: 'ADD_SHIPPING_ADDRESS' });
    }
  };

  const handleEditBillToClicked = async (): Promise<void> => {
    if (cart.billingAddress.id) {
      rootStore.send({ type: 'EDIT_BILLING' });
    } else {
      rootStore.send({ type: 'ADD_BILLING_ADDRESS' });
    }
  };

  const handleEditPaymentClicked = (): void => {
    rootStore.send({ type: 'EDIT_PAYMENT' });
  };

  const handleEditPaymentComplete = (): void => {
    rootStore.send({ type: 'EDIT_PAYMENT_COMPLETE' });
  };

  const handleShippingAddressChanged = (
    address: ICustomerShippingAddressModel,
    event: CheckoutEvent
  ): void => {
    rootStore.setShippingAddressOnCart(address as AddressContract);
    rootStore.send(event);
  };

  const handleBillingAddressChanged = (
    address: ICustomerBillingAddressModel,
    event: CheckoutEvent
  ): void => {
    cart.updateBillingAddress(address as AddressContract);
    rootStore.send(event);
  };

  const renderView = (): ReactElement | null => {
    if (customer.isLoading) {
      return null;
    }
    // if stateMatches or currentStep corresponds to an edit view, return it instead
    if (currentStep === 'OneClick.Payment') {
      return (
        <EditPaymentView
          onScrollTopOfPage={scrollTopOfPage}
          onEditComplete={handleEditPaymentComplete}
        />
      );
    }
    if (rootStore.stateMatches('OneClick.ShippingAddress')) {
      return <EditShipToView onAddressChanged={handleShippingAddressChanged} />;
    }
    if (rootStore.stateMatches('OneClick.BillingAddress')) {
      return <EditBillToView onAddressChanged={handleBillingAddressChanged} />;
    }
    return (
      <OneClickView
        onLogout={handleLogoutClick}
        onEditShipTo={handleEditShipToClicked}
        onEditBillTo={handleEditBillToClicked}
        onEditPayment={handleEditPaymentClicked}
        onScrollTopOfPage={scrollTopOfPage}
      />
    );
  };

  return (
    <CheckoutPageContainer cart={<CartSummarySection />} ref={checkoutDivRef}>
      <Header
        logoText={configuration.logo.text ?? ''}
        logoPublicId={configuration.logo.publicId ?? undefined}
      />
      <Divider className="md:hidden" />
      {renderView()}
      <Footer showTermsAndConditions />
    </CheckoutPageContainer>
  );
});

export default OneClickPage;
